import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { getAuth, applyActionCode, checkActionCode } from "firebase/auth";
import { useToast } from "../../hooks/useToast";
import { updateUserEmail } from "../../services/apiservices/email";
import { resendOtp } from "../../services/apiservices/email";
import { getEmployee } from "../../services/apiservices/employee";
import { Mail, AlertCircle } from "lucide-react";
import csmGrayTextLogin from "../../assets/images/csmGrayTextLogin.png";

const VerifyNewEmail = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const auth = getAuth();

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(300);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState("");

  const [userData, setUserData] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [statusText, setStatusText] = useState("Verifying your email...");

  const inputRefs = useRef([]);
  const queryParameters = new URLSearchParams(window.location.search);
  const oobCode = queryParameters.get("oobCode");

  useEffect(() => {
    const userIdParam = queryParameters.get("userId");

    if (userIdParam) {
      setUserId(userIdParam);
      fetchUserData(userIdParam);
    }
  }, []);

  const fetchUserData = async (uid) => {
    try {
      getEmployee(uid, (res) => {
        const data = res?.data;
        setUserData(data);
      });
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load user information");
      toast?.({
        title: "Error",
        description: "Failed to load user information",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);

    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6).split("");

    if (pastedData.some((char) => !/^\d+$/.test(char))) return;

    const newOtp = [...otp];
    pastedData.forEach((char, idx) => {
      if (idx < 6) newOtp[idx] = char;
    });
    setOtp(newOtp);

    const lastFilledIndex = Math.min(pastedData.length, 6) - 1;
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleVerifyOTP = async () => {
    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      setError("Please enter complete OTP");
      return;
    }

    if (timeLeft <= 0) {
      setError("OTP has expired. Please request a new one.");
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      if (otpCode === userData?.emailChangeOtp?.toString()) {
        setLoading(true);
        const info = await checkActionCode(auth, oobCode);
        await applyActionCode(auth, oobCode);

        const newEmail = info?.data?.email;
        await updateUserEmail(newEmail);

        setStatusText("Email verified! Redirecting...");
        toast?.({
          title: "Success",
          description: "Email updated successfully!",
          variant: "default",
        });

        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        setLoading(false);
        setError("Invalid OTP");
      }
    } catch (err) {
      console.error(err);
      setStatusText("Verification failed.");
      toast?.({
        title: "Error",
        description: err.message || "Verification failed.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (isResending) return;

    setIsResending(true);
    setError("");
    setOtp(["", "", "", "", "", ""]);

    try {
      if (userData?.superUserRequest === true) {
        resendOtp(userData?.pendingEmail, userData?.firstName, userData?.id, true);
      } else {
        resendOtp(userData?.email, userData?.firstName, userData?.id, false);
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));

      await getEmployee(userData?.id, (res) => {
          const data = res?.data;
          setUserData(data);
      });

      setTimeLeft(300);

      toast?.({
        title: "OTP Sent",
        description: "A new OTP has been sent to your email",
        variant: "default",
      });

      inputRefs.current[0]?.focus();
    } catch (err) {
      console.error("Resend error:", err);
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
  <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-gray-50 to-gray-100 p-16">
    
    <img
      src={csmGrayTextLogin}
      alt="ProTeams Logo"
      className="mb-30 object-contain"
      style={{height:"70px", width: "290px"}}
    />

    <div className="bg-white rounded-xl p-40 shadow-lg text-center max-w-md w-full">
      
      {loading ? (
        <div className="text-center">
          <svg
            width="48"
            height="48"
            viewBox="0 0 50 50"
            className="animate-spin mx-auto mb-10 text-green-600"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#16a34a"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="31.4 31.4"
              transform="rotate(-90 25 25)"
            />
          </svg>
          <p className="text-gray-800 font-medium text-base mb-8">
            Please do not close or reload this page.
          </p>
          <p className="text-gray-600 text-sm mb-24">
            We are working to complete your request…
          </p>

          <h2 className="text-green-700 font-semibold tracking-wide text-lg mt-12">
            {statusText}
          </h2>
        </div>
      ) : (
        <>
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-32 rounded-2xl mb-24 shadow-sm">
            <div className="text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-16">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-8">
                Email verification
              </h2>
              <p className="text-gray-600 text-sm">
                We've sent a verification code to
              </p>
              <p className="text-gray-800 font-semibold mt-4">
                {userData?.email || "your email"}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-24">
            {error && (
              <div className="mb-4 p-12 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-2" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex justify-center gap-2 mb-24" onPaste={handlePaste}>
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  disabled={isVerifying || timeLeft <= 0}
                  className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all bg-white shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              ))}
            </div>

            <button
              onClick={handleVerifyOTP}
              disabled={otp.some((d) => !d) || isVerifying || timeLeft <= 0}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all mb-4"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Verifying...
                </span>
              ) : timeLeft <= 0 ? (
                "OTP Expired"
              ) : (
                "Verify Code"
              )}
            </button>

            <div className="flex justify-between items-center text-sm">
              <span
                className={`font-medium ${
                  timeLeft <= 30 ? "text-red-600" : "text-gray-500"
                }`}
              >
                {timeLeft > 0
                  ? `Expires in ${formatTime(timeLeft)}`
                  : "OTP Expired"}
              </span>
              <button
                onClick={handleResendOTP}
                disabled={isResending || timeLeft > 0}
                className="text-purple-600 font-medium hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isResending ? "Sending..." : "Resend Code"}
              </button>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-16">
            Didn't receive the code? Check your spam folder or click resend after
            the timer expires.
          </p>
        </>
      )}
    </div>
  </div>
);
};

export default VerifyNewEmail;